import { QueryClientProvider } from "@tanstack/react-query";
import { Howl } from "howler";
import { AnimatePresence, motion } from "motion/react";
import type React from "react";
import { useCallback, useEffect, useRef } from "react";

import { useCreateQueryClient } from "~/hooks/query";
import { useLocalStorage } from "~/hooks/storage";

import { Game } from "./Game";
import { GameContext, type GameType, type Sound, sprite } from "./GameContext";
import { PostGame } from "./PostGame";

export const GameProvider: React.FC<{
  game: GameType;
  setGame: React.Dispatch<React.SetStateAction<GameType>>;
  header: React.ReactElement;
  children: React.ReactElement;
  onCompleted: (gameState: GameType) => void;
}> = ({ game, setGame, children, header, onCompleted }) => {
  const howl = useRef<Howl>(null);
  const queryClient = useCreateQueryClient();

  useEffect(() => {
    howl.current = new Howl({
      volume: 0.5,
      // TODO: wire up classic sounds
      src: "/sounds/2025-07-14.mp3",
      sprite,
    });

    return () => {
      // destroy the howler instance and stop sounds when the component unmounts
      howl.current?.unload();
    };
  }, []);

  const resetGame = useCallback(() => {
    setGame((prev) => ({
      ...prev,
      time: 0,
      words: [],
      answers: [],
    }));
  }, [setGame]);

  const [isSoundEnabled, setIsSoundEnabled] = useLocalStorage("sound", true);

  const playSound = useCallback(
    (sound: Sound) => {
      if (isSoundEnabled || sound === "on") {
        howl.current?.play(sound);
      }
    },
    [isSoundEnabled],
  );

  const toggleSound = useCallback(() => {
    setIsSoundEnabled((prev) => !prev);
    playSound(isSoundEnabled ? "off" : "on");
  }, [isSoundEnabled, setIsSoundEnabled, playSound]);

  return (
    <QueryClientProvider client={queryClient}>
      <GameContext
        value={{
          game,
          header,
          setGame,
          resetGame,
          playSound,
          toggleSound,
          isSoundEnabled,
        }}
      >
        {!game.words.length ? (
          children
        ) : (
          <AnimatePresence initial={false} mode="popLayout">
            <motion.div
              key={game.time}
              variants={{
                hidden: { opacity: 0 },
                visible: { opacity: 1 },
              }}
              initial="hidden"
              animate="visible"
              exit="hidden"
              onAnimationComplete={(definition) => {
                if (definition === "visible") {
                  // the definition is only "visible" when the post-game screen is shown
                  onCompleted(game);
                }
              }}
            >
              {game.time > 0 ? <PostGame header={header} /> : <Game />}
            </motion.div>
          </AnimatePresence>
        )}
      </GameContext>
    </QueryClientProvider>
  );
};
